import { useCallback, useState } from 'react';
import { useSubjectLanguageVariants } from '@features/user';
import {
  PeopleAlt as PeopleIcon,
  Videocam as StartCallIcon,
  Wifi as OnlineIcon,
} from '@mui/icons-material';
import { Badge, Box, ButtonBase, Slide, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';

import type {
  ProviderWaitingRoomRemoveRequest,
  ProviderWaitingRoomRuntime,
  WaitingRoomEntry,
  WaitingRoomProviderAvailability,
} from '../types';
import { getWaitingRoomRemoveErrorMessage } from '../utils';

import { WaitingRoomEntryActionButtons } from './WaitingRoomEntryActionButtons';
import { WaitingRoomPeoplePanel } from './WaitingRoomPeoplePanel';
import { WaitingRoomRemoveDialog } from './WaitingRoomRemoveDialog';

const isAdmittableEntry = (entry: WaitingRoomEntry) =>
  entry.current_state === 'DEVICE_CHECK' || entry.current_state === 'IN_QUEUE';

const isWaitingRoomListEntry = (entry: WaitingRoomEntry) =>
  entry.current_state === 'CHECKED_IN' ||
  entry.current_state === 'DEVICE_CHECK' ||
  entry.current_state === 'IN_QUEUE';

const isInCallWaitingRoomEntry = (entry: WaitingRoomEntry) =>
  entry.current_state === 'ADMITTED' || entry.current_state === 'IN_CALL';

const getPrimaryActionLabel = (entry: WaitingRoomEntry) => {
  if (isAdmittableEntry(entry)) {
    return 'Go to encounter';
  }
  if (entry.current_state === 'CHECKED_IN') {
    return 'Check-in in progress';
  }
  return 'Already admitted';
};

interface WaitingRoomFloatingPanelProps {
  waitingRoom: ProviderWaitingRoomRuntime;
  onStartVideoCall?: (entry: WaitingRoomEntry) => void;
}

export function WaitingRoomFloatingPanel({
  waitingRoom,
  onStartVideoCall,
}: WaitingRoomFloatingPanelProps) {
  const { enqueueSnackbar } = useSnackbar();
  const subjectLanguage = useSubjectLanguageVariants();
  const [isOpen, setIsOpen] = useState(false);
  const [isCopyingLink, setIsCopyingLink] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [entryPendingRemoval, setEntryPendingRemoval] = useState<WaitingRoomEntry | null>(null);

  const waitingEntries = waitingRoom.entries.filter(isWaitingRoomListEntry);
  const inCallParticipants = waitingRoom.entries.filter(isInCallWaitingRoomEntry).map(entry => ({
    id: entry.waiting_room_queue_entry_id,
    name: entry.patient_name,
    subtitle: 'In call',
  }));
  const waitingCount = waitingEntries.length;
  const hasWaitingEntries = waitingCount > 0;
  const defaultExpandedEntryId =
    waitingEntries.find(isAdmittableEntry)?.waiting_room_queue_entry_id ??
    waitingEntries[0]?.waiting_room_queue_entry_id;

  const handleCopyLink = useCallback(async () => {
    const shortUrl = waitingRoom.link?.short_url;
    if (!shortUrl) {
      enqueueSnackbar('Waiting room link is not available yet.', { variant: 'warning' });
      return;
    }

    setIsCopyingLink(true);
    try {
      await navigator.clipboard.writeText(shortUrl);
      setLinkCopied(true);
      enqueueSnackbar('Waiting room link copied.', { variant: 'success' });
      window.setTimeout(() => setLinkCopied(false), 1500);
    } catch {
      enqueueSnackbar('Could not copy waiting room link.', { variant: 'error' });
    } finally {
      setIsCopyingLink(false);
    }
  }, [enqueueSnackbar, waitingRoom.link?.short_url]);

  const handleSetAvailability = useCallback(
    async (availability: WaitingRoomProviderAvailability) => {
      try {
        await waitingRoom.setAvailability(availability);
      } catch {
        enqueueSnackbar('Could not update waiting room availability.', { variant: 'error' });
      }
    },
    [enqueueSnackbar, waitingRoom],
  );

  const requestRemoveFromWaitingRoom = useCallback((entry: WaitingRoomEntry) => {
    setEntryPendingRemoval(entry);
  }, []);

  const handleConfirmRemoveFromWaitingRoom = useCallback(
    async (request: ProviderWaitingRoomRemoveRequest) => {
      if (!entryPendingRemoval) {
        return;
      }

      try {
        await waitingRoom.removeQueueEntry(
          entryPendingRemoval.waiting_room_queue_entry_id,
          request,
        );
        enqueueSnackbar(`${entryPendingRemoval.patient_name} was removed from the waiting room.`, {
          variant: 'success',
        });
        setEntryPendingRemoval(null);
      } catch (error) {
        await waitingRoom.refreshQueue().catch(() => undefined);
        enqueueSnackbar(getWaitingRoomRemoveErrorMessage(error, subjectLanguage), {
          variant: 'error',
        });
      }
    },
    [enqueueSnackbar, entryPendingRemoval, subjectLanguage, waitingRoom],
  );

  if (!waitingRoom.hasAccess) {
    return null;
  }

  return (
    <>
      <Slide direction="left" in={isOpen} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: 'fixed',
            top: 24,
            right: 24,
            bottom: 24,
            zIndex: 1298,
            width: 'min(380px, calc(100vw - 48px))',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            overflow: 'hidden',
          }}
        >
          <WaitingRoomPeoplePanel
            entries={waitingEntries}
            providerAvailability={waitingRoom.providerAvailability}
            sseConnectionState={waitingRoom.sseConnectionState}
            link={waitingRoom.link}
            error={waitingRoom.error}
            isSettingAvailability={waitingRoom.isSettingAvailability}
            isCopyingLink={isCopyingLink}
            linkCopied={linkCopied}
            emptyLabel={`No ${subjectLanguage.plural} waiting.`}
            defaultExpandedEntryId={defaultExpandedEntryId}
            inCallParticipants={inCallParticipants}
            onRemoveInCallParticipant={participant => {
              const entry = waitingRoom.entries.find(
                waitingRoomEntry => waitingRoomEntry.waiting_room_queue_entry_id === participant.id,
              );
              if (entry) {
                requestRemoveFromWaitingRoom(entry);
              }
            }}
            entryExpandedContent={entry => (
              <WaitingRoomEntryActionButtons
                primaryLabel={getPrimaryActionLabel(entry)}
                primaryIcon={<StartCallIcon fontSize="small" />}
                onPrimaryAction={
                  onStartVideoCall && !waitingRoom.isAdmitting && isAdmittableEntry(entry)
                    ? () => {
                        setIsOpen(false);
                        onStartVideoCall(entry);
                      }
                    : undefined
                }
                onRemoveFromWaitingRoom={
                  waitingRoom.isRemoving ? undefined : () => requestRemoveFromWaitingRoom(entry)
                }
              />
            )}
            onSetAvailability={handleSetAvailability}
            onCopyLink={handleCopyLink}
            onClose={() => setIsOpen(false)}
          />
        </Box>
      </Slide>

      {!isOpen && (
        <Box
          sx={{
            position: 'fixed',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1298,
          }}
        >
          <ButtonBase
            aria-label={
              hasWaitingEntries ? `Open waiting room, ${waitingCount} waiting` : 'Open waiting room'
            }
            onClick={() => setIsOpen(true)}
            sx={{
              width: 56,
              minHeight: 176,
              borderRadius: '12px 0 0 12px',
              bgcolor: 'background.paper',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              border: '1px solid',
              borderRight: 0,
              borderColor: hasWaitingEntries ? 'warning.main' : 'divider',
              px: 1,
              py: 1.5,
              textAlign: 'center',
              transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
              '&:hover': {
                bgcolor: 'background.paper',
                borderColor: hasWaitingEntries ? 'warning.dark' : 'primary.main',
                boxShadow: '0 10px 36px rgba(0, 0, 0, 0.28)',
                transform: 'translateX(-4px)',
              },
            }}
          >
            <Stack spacing={1} sx={{ alignItems: 'center', justifyContent: 'center' }}>
              <Badge
                badgeContent={waitingCount}
                invisible={!hasWaitingEntries}
                sx={{
                  '& .MuiBadge-badge': {
                    bgcolor: 'warning.main',
                    color: 'warning.contrastText',
                    fontWeight: 800,
                  },
                }}
              >
                <PeopleIcon color={hasWaitingEntries ? 'warning' : 'action'} />
              </Badge>
              <Typography
                sx={{
                  fontWeight: 800,
                  lineHeight: 1,
                  writingMode: 'vertical-rl',
                  transform: 'rotate(180deg)',
                }}
              >
                Waiting room
              </Typography>
              <Stack spacing={0.5} sx={{ alignItems: 'center' }}>
                <OnlineIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor:
                      waitingRoom.providerAvailability === 'online'
                        ? 'success.main'
                        : 'text.disabled',
                  }}
                />
              </Stack>
            </Stack>
          </ButtonBase>
        </Box>
      )}

      <WaitingRoomRemoveDialog
        open={Boolean(entryPendingRemoval)}
        entry={entryPendingRemoval}
        isSubmitting={waitingRoom.isRemoving}
        onClose={() => setEntryPendingRemoval(null)}
        onConfirm={handleConfirmRemoveFromWaitingRoom}
      />
    </>
  );
}
